import React, { useEffect, useState } from 'react';
import { getMapData } from "@mappedin/mappedin-js";
import { useMappedInSettings } from '../../hooks/useMappedInSettings';
import { useParams } from 'react-router-dom';
import {
    Box,
    Typography,
    CircularProgress,
    Chip,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Alert,
    Collapse,
    IconButton,
    Button,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

const DummyCategory = () => {
    const { userId } = useParams();
    
    // Fetch MappedIn settings dynamically
    const { settings: mappedInConfig, loading: configLoading } = useMappedInSettings('697a85e15294774d5b98e08a');
    
    const [locationCategories, setLocationCategories] = useState([]);
    const [emptyCategories, setEmptyCategories] = useState([]);
    const [tags, setTags] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [openRows, setOpenRows] = useState({});
    const [openTagRows, setOpenTagRows] = useState({});

    useEffect(() => {
        if (!configLoading && mappedInConfig) {
            fetchMapData();
        }
    }, [configLoading, mappedInConfig]);

    const fetchMapData = async () => {
        if (!mappedInConfig?.key || !mappedInConfig?.secret || !mappedInConfig?.mapId) {
            setError('MappedIn configuration not found. Please configure in Map Settings.');
            setLoading(false);
            return;
        }
        
        try {
            setLoading(true);
            const options = {
                key: mappedInConfig.key,
                secret: mappedInConfig.secret,
                mapId: mappedInConfig.mapId,
            };
            const mapData = await getMapData(options);
            
            // Get all enterprise categories
            const categories = mapData.getByType("enterprise-category");
            
            // Get all enterprise locations for tags
            const enterpriseLocations = mapData.getByType("enterprise-venue");

            console.log("enterprise locations",enterpriseLocations )

            // Alphabetically sort all categories
            const sortedCategories = [
                ...categories.sort((a, b) => (a.name > b.name ? 1 : -1))
            ];

            // Separate categories with and without locations
            const withLocations = [];
            const withoutLocations = [];

            sortedCategories.forEach(category => {
                const sortedLocations = category.locations 
                    ? [...category.locations].sort((a, b) => (a.name > b.name ? 1 : -1))
                    : [];

                const processedCategory = {
                    ...category,
                    sortedLocations,
                    locationCount: sortedLocations.length
                };

                if (sortedLocations.length > 0) {
                    withLocations.push(processedCategory);
                } else {
                    withoutLocations.push(processedCategory);
                }
            });

            // Extract and process tags from locations
            const tagMap = {};
            enterpriseLocations.forEach(loc => {
                const locTags = loc.tags || [];
                locTags.forEach(tag => {
                    const tagName = typeof tag === 'string' ? tag : (tag.name || tag);
                    if (!tagName || !tagName.trim()) return;
                    
                    const key = tagName.trim();
                    if (!tagMap[key]) {
                        tagMap[key] = {
                            name: key,
                            locations: []
                        };
                    }
                    tagMap[key].locations.push({
                        name: loc.name?.trim() || '',
                        externalId: loc.externalId || '',
                        description: loc.description || ''
                    });
                });
            });

            // Convert to array and sort alphabetically
            const tagsArray = Object.values(tagMap)
                .map(tag => ({
                    ...tag,
                    locationCount: tag.locations.length,
                    locations: tag.locations.sort((a, b) => a.name.localeCompare(b.name))
                }))
                .sort((a, b) => a.name.localeCompare(b.name));

            console.log("Categories with locations:", withLocations);
            console.log("Categories without locations:", withoutLocations);
            console.log("Tags with locations:", tagsArray);
            
            setLocationCategories(withLocations);
            setEmptyCategories(withoutLocations);
            setTags(tagsArray);
            setError(null);
        } catch (err) {
            console.error("Error fetching map data:", err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleToggleRow = (categoryId) => {
        setOpenRows(prev => ({
            ...prev,
            [categoryId]: !prev[categoryId]
        }));
    };

    const handleToggleTagRow = (tagName) => {
        setOpenTagRows(prev => ({
            ...prev,
            [tagName]: !prev[tagName]
        }));
    };

    const exportToCSV = () => {
        const rows = [];
        
        // Header
        rows.push(['Category Name', 'Location Count', 'Location Name', 'Location ID', 'Description']);
        
        // Categories with locations
        locationCategories.forEach(category => {
            category.sortedLocations.forEach((location, index) => {
                rows.push([
                    index === 0 ? category.name : '',
                    index === 0 ? category.locationCount : '',
                    location.name,
                    location.externalId || '',
                    location.description || ''
                ]);
            });
        });
        
        // Empty categories
        if (emptyCategories.length > 0) {
            rows.push(['', '', '', '', '']); // Empty row
            rows.push(['EMPTY CATEGORIES', '', '', '', '']);
            emptyCategories.forEach(category => {
                rows.push([category.name, '0', 'No locations', '', '']);
            });
        }
        
        const csvContent = rows.map(row => 
            row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
        ).join('\n');
        
        const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        
        // Generate timestamp with date and time
        const now = new Date();
        const dateStr = now.toISOString().slice(0, 10); // YYYY-MM-DD
        const timeStr = now.toTimeString().slice(0, 8).replace(/:/g, '-'); // HH-MM-SS
        
        a.download = `mappedin_categories_${dateStr}_${timeStr}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px" flexDirection="column" gap={2}>
                <CircularProgress size={60} />
                <Typography variant="h6" color="text.secondary">
                    Loading MappedIn Categories...
                </Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <Paper elevation={3} sx={{ p: 4, maxWidth: 500, textAlign: 'center' }}>
                    <Typography variant="h5" color="error" gutterBottom>
                        Error Loading Data
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        {error}
                    </Typography>
                </Paper>
            </Box>
        );
    }

    const totalLocations = locationCategories.reduce((sum, cat) => sum + cat.locationCount, 0);
    const totalTaggedLocations = tags.reduce((sum, tag) => sum + tag.locationCount, 0);

    return (
        <Box className="main-container" sx={{ paddingLeft: "280px", pt: 10, backgroundColor: '#f5f7fa', minHeight: '100vh' }}>
            <div className="pd-ltr-20 xs-pd-20-10">

                {/* Header */}
                <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: '#1a1a2e' }}>
                            MappedIn Location Categories
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                            Complete list of all categories and their locations
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <Chip
                                icon={<CheckCircleIcon />}
                                label={`${locationCategories.length} Categories with Locations`}
                                color="success"
                            />
                            <Chip
                                icon={<WarningAmberIcon />}
                                label={`${emptyCategories.length} Empty Categories`}
                                color="warning"
                            />
                            <Chip
                                label={`${totalLocations} Total Locations`}
                                color="primary"
                            />
                            <Chip
                                label={`${tags.length} Tags`}
                                color="info"
                            />
                        </Box>
                    </Box>
                    {/* <Button
                        variant="contained"
                        startIcon={<FileDownloadIcon />}
                        onClick={exportToCSV}
                        sx={{
                            backgroundColor: '#1a1a2e',
                            '&:hover': { backgroundColor: '#16213e' }
                        }}
                    >
                        Export CSV
                    </Button> */}
                </Box>

                {/* Categories with Locations Table */}
                <Paper sx={{ mb: 4, borderRadius: 2, overflow: 'hidden' }}>
                    <Box sx={{ p: 2, backgroundColor: '#1a1a2e', color: '#fff' }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
                            Categories with Locations
                        </Typography>
                    </Box>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                                    <TableCell sx={{ width: 50 }} />
                                    <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem' }}>Category Name</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.9rem', width: 150 }}>
                                        Location Count
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {locationCategories.map((category, index) => (
                                    <React.Fragment key={category.id || index}>
                                        <TableRow 
                                            sx={{ 
                                                '&:hover': { backgroundColor: '#f8f9fa' },
                                                cursor: 'pointer',
                                                backgroundColor: index % 2 === 0 ? '#fff' : '#fafbfc'
                                            }}
                                            onClick={() => handleToggleRow(category.id || index)}
                                        >
                                            <TableCell>
                                                <IconButton size="small">
                                                    {openRows[category.id || index] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                                </IconButton>
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                                                {category.name || 'Unnamed Category'}
                                            </TableCell>
                                            <TableCell align="center">
                                                <Chip 
                                                    label={category.locationCount} 
                                                    size="small" 
                                                    color="primary"
                                                    sx={{ fontWeight: 600 }}
                                                />
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={3}>
                                                <Collapse in={openRows[category.id || index]} timeout="auto" unmountOnExit>
                                                    <Box sx={{ margin: 2 }}>
                                                        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: '#1a1a2e' }}>
                                                            Locations in {category.name}
                                                        </Typography>
                                                        <Table size="small">
                                                            <TableHead>
                                                                <TableRow sx={{ backgroundColor: '#e3f2fd' }}>
                                                                    <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                                                                    <TableCell sx={{ fontWeight: 600 }}>Location Name</TableCell>
                                                                    <TableCell sx={{ fontWeight: 600 }}>Location ID</TableCell>
                                                                    <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                                                                </TableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {category.sortedLocations.map((location, lIndex) => (
                                                                    <TableRow key={location.id || lIndex}>
                                                                        <TableCell sx={{ width: 50 }}>{lIndex + 1}</TableCell>
                                                                        <TableCell sx={{ fontWeight: 500 }}>{location.name}</TableCell>
                                                                        <TableCell>
                                                                            <code style={{ 
                                                                                backgroundColor: '#f0f0f0', 
                                                                                padding: '2px 6px', 
                                                                                borderRadius: 4,
                                                                                fontSize: '0.85rem'
                                                                            }}>
                                                                                {location.externalId || 'N/A'}
                                                                            </code>
                                                                        </TableCell>
                                                                        <TableCell sx={{ fontSize: '0.85rem', color: '#666' }}>
                                                                            {location.description || '—'}
                                                                        </TableCell>
                                                                    </TableRow>
                                                                ))}
                                                            </TableBody>
                                                        </Table>
                                                    </Box>
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                    </React.Fragment>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

                {/* Empty Categories Alert */}
                {emptyCategories.length > 0 && (
                    <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
                        <Box sx={{ p: 2, backgroundColor: '#ff9800', color: '#fff' }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <WarningAmberIcon />
                                Categories Without Locations
                            </Typography>
                        </Box>
                        <Alert severity="warning" sx={{ borderRadius: 0, mb: 0 }}>
                            The following {emptyCategories.length} categories do not have any locations assigned to them.
                        </Alert>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: '#fff3e0' }}>
                                        <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem', width: 80 }}>#</TableCell>
                                        <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem' }}>Category Name</TableCell>
                                        <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem' }}>Status</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {emptyCategories.map((category, index) => (
                                        <TableRow 
                                            key={category.id || index}
                                            sx={{ 
                                                backgroundColor: index % 2 === 0 ? '#fff' : '#fffbf5',
                                                '&:hover': { backgroundColor: '#fff3e0' }
                                            }}
                                        >
                                            <TableCell>{index + 1}</TableCell>
                                            <TableCell sx={{ fontWeight: 500 }}>
                                                {category.name || 'Unnamed Category'}
                                            </TableCell>
                                            <TableCell>
                                                <Chip 
                                                    label="No Locations" 
                                                    size="small" 
                                                    color="warning"
                                                    icon={<WarningAmberIcon />}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                )}

                {/* Tags Section */}
                {tags.length > 0 && (
                    <Paper sx={{ mt: 4, borderRadius: 2, overflow: 'hidden' }}>
                        <Box sx={{ p: 2, backgroundColor: '#2196f3', color: '#fff' }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
                                Location Tags
                            </Typography>
                        </Box>
                        <Alert severity="info" sx={{ borderRadius: 0, mb: 0 }}>
                            {tags.length} tags found across {totalTaggedLocations} location assignments
                        </Alert>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: '#e3f2fd' }}>
                                        <TableCell sx={{ width: 50 }} />
                                        <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem' }}>Tag Name</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.9rem', width: 150 }}>
                                            Location Count
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {tags.map((tag, index) => (
                                        <React.Fragment key={tag.name}>
                                            <TableRow 
                                                sx={{ 
                                                    '&:hover': { backgroundColor: '#e3f2fd' },
                                                    cursor: 'pointer',
                                                    backgroundColor: index % 2 === 0 ? '#fff' : '#fafbfc'
                                                }}
                                                onClick={() => handleToggleTagRow(tag.name)}
                                            >
                                                <TableCell>
                                                    <IconButton size="small">
                                                        {openTagRows[tag.name] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                                    </IconButton>
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                                                    {tag.name}
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Chip 
                                                        label={tag.locationCount} 
                                                        size="small" 
                                                        color="info"
                                                        sx={{ fontWeight: 600 }}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={3}>
                                                    <Collapse in={openTagRows[tag.name]} timeout="auto" unmountOnExit>
                                                        <Box sx={{ margin: 2 }}>
                                                            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: '#1a1a2e' }}>
                                                                Locations tagged with "{tag.name}"
                                                            </Typography>
                                                            <Table size="small">
                                                                <TableHead>
                                                                    <TableRow sx={{ backgroundColor: '#e3f2fd' }}>
                                                                        <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                                                                        <TableCell sx={{ fontWeight: 600 }}>Location Name</TableCell>
                                                                        <TableCell sx={{ fontWeight: 600 }}>Location ID</TableCell>
                                                                        <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                                                                    </TableRow>
                                                                </TableHead>
                                                                <TableBody>
                                                                    {tag.locations.map((location, lIndex) => (
                                                                        <TableRow key={`${tag.name}-${location.externalId || lIndex}`}>
                                                                            <TableCell sx={{ width: 50 }}>{lIndex + 1}</TableCell>
                                                                            <TableCell sx={{ fontWeight: 500 }}>{location.name}</TableCell>
                                                                            <TableCell>
                                                                                <code style={{ 
                                                                                    backgroundColor: '#f0f0f0', 
                                                                                    padding: '2px 6px', 
                                                                                    borderRadius: 4,
                                                                                    fontSize: '0.85rem'
                                                                                }}>
                                                                                    {location.externalId || 'N/A'}
                                                                                </code>
                                                                            </TableCell>
                                                                            <TableCell sx={{ fontSize: '0.85rem', color: '#666' }}>
                                                                                {location.description || '—'}
                                                                            </TableCell>
                                                                        </TableRow>
                                                                    ))}
                                                                </TableBody>
                                                            </Table>
                                                        </Box>
                                                    </Collapse>
                                                </TableCell>
                                            </TableRow>
                                        </React.Fragment>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                )}

                {locationCategories.length === 0 && emptyCategories.length === 0 && tags.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                        <Typography variant="h6" color="text.secondary">
                            No categories or tags found
                        </Typography>
                    </Box>
                )}
            </div>
        </Box>
    );
};

export default DummyCategory;
